import { css } from 'astroturf';
import { StaticQuery, graphql } from 'gatsby';
import React from 'react';
import { Close } from 'react-bytesize-icons';

import { countries, needZipCode } from './helpers';
import MobilePrice from './mobilePrice';

// const CHECK_URL = `http://localhost:3000/api/chksrxsts`;
const CHECK_URL = `https://dashboard.polypane.app/api/chksrxsts`;

const SHOW = {
  PADDLE: 'paddle',
  FORM: 'form',
  TRIAL: 'trial',
};

const tagline = {
  Individual: 'Perfect for freelancers, solo devs or personal projects.',
  Business: 'Use Polypane with your entire team.',
};

const styles = css`
  .container {
    position: fixed;
    inset: 0;
    background: #0c628d;
    z-index: 100;
    display: block;
    text-align: left;
    overflow: auto;
    pointer-events: none;
    opacity: 0;

    &.visible {
      display: block;
      pointer-events: all;
      opacity: 1;
      transition: 0.15s ease-in-out opacity;

      @media (min-width: 60rem) {
        display: grid;
        height: 100vh;
        grid-template-columns: 1fr 1fr;
        grid-template-areas: 'form info';
      }
    }
  }

  .close {
    position: absolute;
    right: 0;
    top: 0;
    border: 0;
    background: 0;
    color: #fff;
    padding: 1rem;
    opacity: 0.5;
    transition: 0.25s ease-in-out opacity;

    &:hocus {
      opacity: 1;
    }
  }

  .title {
    display: none;
    align-items: center;
    gap: 1rem;
    font-weight: bold;
    font-size: 2rem;
    line-height: 1;
    margin-bottom: 1rem;

    img {
      width: 36px;
    }
  }

  .info {
    padding: 2rem 1.5rem;
    color: #fff;
    background: linear-gradient(135deg, #0c628d, ease-in-out, #4da758);
    grid-area: info;

    > div {
      max-width: 420px;
      margin: 0 auto;
    }

    @media (min-width: 60rem) {
      padding-top: 6rem;
      padding-left: 6rem;

      > div {
        margin: 0;
      }
    }

    .title {
      display: flex;
      @media (min-width: 60rem) {
        display: none;
      }
    }

    .orderBlock {
      display: flex;
      flex-direction: column;
      gap: 0.5rem;
      text-box-trim: trim-both;

      @media (min-width: 60rem) {
        margin-bottom: 4.2rem;
      }

      img {
        max-width: 64px;
      }

      > small {
        font-size: 0.8125rem;
        text-align: left;
        line-height: 1;
        color: color-mix(in hsl, currentColor 75%, transparent);
      }

      > div {
        width: 100%;
        line-height: 1.2;
        display: flex;
        flex-direction: row;
        justify-content: space-between;
        gap: 0.25rem;
        margin-bottom: 0.5rem;
        white-space: word-break;
        word-break: break-word;
        text-align: left;

        & div:last-child {
          text-align: right;
          flex-shrink: 0;
        }

        small {
          opacity: 1;
          font-weight: normal;
          font-size: 1rem;
          line-height: 1;
          text-align: right;
          color: color-mix(in hsl, currentColor 75%, transparent);
          max-width: 100%;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        b {
          font-size: 24px;
          display: block;
          text-align: right;
          font-weight: 600;
        }
      }

      .shortdesc {
        line-height: 1.25;
        color: color-mix(in hsl, currentColor 75%, transparent);
        text-wrap: balance;
      }
    }

    table {
      position: relative;
      display: none;

      width: 100%;

      caption {
        position: absolute;
        right: 0;
        top: -1.5rem;
        font-size: 0.8rem;
      }

      tr {
        background: #0004;
        padding: 0 1rem;
        display: flex;
        justify-content: space-between;
        margin-bottom: 1px;

        &:first-child {
          border-radius: 5px 5px 0 0;
        }

        &.sales {
          color: color-mix(in hsl, currentColor 75%, transparent);
        }
        &:last-of-type {
          border-radius: 0 0 5px 5px;

          td {
            font-weight: 600;
          }
        }
      }
      th {
        padding: 1rem 0;
        text-align: left;
        font-weight: 500;

        em {
          font-style: normal;
          font-weight: normal;
        }
      }
      td {
        padding: 1rem 0;
        text-align: right;
      }

      @media (min-width: 60rem) {
        display: block;
      }
    }

    span em {
      font-size: 0.875rem;
      display: none;
      padding: 0.5rem 1rem 0 1rem;
      color: color-mix(in hsl, currentcolor 75%, transparent);
      line-height: 1.25;
      opacity: 1;
      transition: 0.25s ease-in-out opacity;

      @media (min-width: 60rem) {
        display: block;

        &[aria-hidden='true'] {
          opacity: 0;
        }
      }
    }
  }

  .form {
    padding: 2rem 1rem;
    background: #fff;
    min-height: 600px;
    grid-area: form;
    position: relative;
    overflow: auto;
    padding-bottom: 6rem;

    @media (min-width: 60rem) {
      display: flex;
      justify-content: flex-end;
      padding-right: 6rem;
      padding-top: 6rem;
      height: 100%;
      max-height: 100%;
    }

    .steps {
      font-size: 0.8125rem;
      color: color-mix(in hsl, currentColor 60%, transparent);
      margin-bottom: 1.4rem;

      b {
        color: #000;
      }
    }

    .title {
      display: none;
      @media (min-width: 60rem) {
        display: flex;
      }
    }

    h4 {
      line-height: 1;
      text-wrap: wrap;
    }

    & .checkoutContainer,
    & > div {
      position: relative;
      width: 100%;
      max-width: 400px;
      margin: 0 auto;

      @media (min-width: 60rem) {
        margin: 0;
      }

      &.paddleArea {
        padding: 1rem 0 0;
        min-height: 40px;
        z-index: 1;

        &.hidden {
          padding: 0;
          min-height: 0;
        }
        & iframe {
          overflow: hidden;
        }
      }

      &.error {
        pointer-events: none;
        position: absolute;
        left: 0;
        top: 180px;

        & > * {
          padding: 0 1rem;
        }
      }

      &.noaccount {
        & p {
          margin: 1rem 0;
        }

        & strong strong {
          font-weight: normal;
        }

        & div {
          display: flex;
          align-items: center;
          gap: 1rem;
        }
        & a {
          position: relative;
          text-align: center;
          font-weight: 600;
          font-size: 1rem;
          border: 0;
          border-radius: 0.3rem;
          margin: 0 1rem 0 0;
          height: 2.5rem;
          line-height: 2rem;
          overflow: hidden;
          background: none;
          box-shadow: none;
          text-decoration: underline;
          flex: 0 1 180px;

          &:hocus {
            color: #000;
          }
        }
      }

      & .error {
        margin: 1rem 0;
        text-align: center;
        font-weight: bold;
      }
    }

    & legend {
      font-size: 1.25rem;
      color: #107db5;
      font-weight: 600;
      width: 100%;
      line-height: 1;
      margin: 0;
      padding: 0;

      & small {
        display: block;
        font-weight: normal;
        font-size: 1rem;
        line-height: 1.5;
      }
    }
    & p {
      margin: 1rem 0;
      line-height: 1.5;
    }

    & label {
      display: block;
      text-align: left;
      margin: 1.5rem 0 0.25rem;
      font-weight: normal;
    }

    & input,
    & textarea {
      font-size: 1rem;
      padding: 0.5rem 1rem;
      width: 100%;
      background: rgb(255, 255, 255);
      border-color: rgb(204, 204, 204);
      border-width: 1px;
      border-style: solid;
      border-radius: 0.3rem;

      &:focus-visible {
        outline-color: #4ca2cd;
        outline-offset: 0.2rem;
      }
    }

    & select {
      font-size: 1rem;
      padding: 0.5rem 0.8rem;
      width: 100%;
      background: rgb(255, 255, 255);
      border-color: rgb(204, 204, 204);
      border-width: 1px;
      border-style: solid;
      border-radius: 0.3rem;

      &:focus-visible {
        outline-color: #4ca2cd;
        outline-offset: 0.2rem;
      }
    }

    & label input {
      width: auto;
    }

    & textarea {
      height: 6rem;
    }

    & .topMargin {
      margin-top: 1rem;
      font-weight: normal;
    }

    & button {
      position: relative;
      background: linear-gradient(135deg, #67b26f, #67b26f);
      text-align: center;
      font-weight: 600;
      font-size: 1.2rem;
      color: #fff;
      border: 0;
      border-radius: 3rem;
      width: 100%;
      min-width: 175px;
      margin: 1rem auto;
      height: 2.5rem;
      transition: background 0.25s ease-in-out;
      overflow: hidden;
      cursor: pointer;

      &:hocus {
        &::before {
          opacity: 1;
        }
      }

      & span {
        position: relative;
        z-index: 1;
      }

      &::before {
        content: ' ';
        display: block;
        background: linear-gradient(135deg, #4ca2cd, #67b26f);
        position: absolute;
        left: 0;
        top: 0;
        width: 100%;
        height: 100%;
        z-index: 0;
        opacity: 0;
        transition: opacity 0.25s ease-in-out;
        border-radius: 0.5rem;
      }
    }

    & small {
      display: block;
      & svg {
        vertical-align: top;
      }
    }
  }

  div.noaccount {
    h4 strong {
      font-weight: normal;
    }
    & p {
      margin: 1rem 0;
    }

    & strong strong {
      font-weight: normal;
    }

    & div {
      display: flex;
      align-items: center;
      gap: 1rem;
    }
    & a {
      position: relative;
      text-align: center;
      font-weight: 600;
      font-size: 1rem;
      border: 0;
      border-radius: 0.3rem;
      margin: 0 1rem 0 0;
      height: 2.5rem;
      line-height: 2rem;
      overflow: hidden;
      background: none;
      box-shadow: none;
      text-decoration: underline;
      flex: 0 1 180px;

      &:hocus {
        color: #000;
      }
    }
  }
`;

class Index extends React.Component {
  constructor(props) {
    super();
    this.state = {
      message: '',
      status: '',
      subscriptionInfo: { country: props.country, email: props.email, postcode: props.postcode },
      personData: { email: props.email, givenname: props.givenname },
      separateBilling: false,
      show: SHOW.FORM,
    };

    this.firstInput;
    this.container;
  }

  async componentDidMount() {
    const params = new URL(document.location).searchParams;

    if (params.get('email')) {
      const getData = await fetch(CHECK_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: params.get('email'), hash: params.get('hash') }),
      }).then((response) => response.json());

      const subscriptionInfo = {
        ...this.state.subscriptionInfo,
        canSubscribe: getData.canSubscribe,
        exist: getData.exists,
        givenname: getData.name,
        email: params.get('email'),
        emp: params.get('emp'),
        country: this.props.country,
      };

      if (getData.canSubscribe) {
        this.setState({
          subscriptionInfo,
          personData: { email: params.get('email'), givenname: getData?.name || '' },
        });
      }
    }
  }

  componentDidUpdate(prevProps, prevState) {
    if (this.props.country !== prevProps.country) {
      this.setState((state) => ({
        subscriptionInfo: { ...state.subscriptionInfo, country: this.props.country },
      }));
    }

    if (this.props.visible !== prevProps.visible) {
      document.body.style.overflow = this.props.visible ? 'clip' : 'auto';

      if (this.props.visible) {
        this.firstInput?.focus();
        window.$crisp?.do('chat:hide');
        if (typeof window !== 'undefined' && window.umami) {
          window.umami.track((data) => ({ ...data, url: '/checkout/', title: 'Paddle Checkout' }));
        }
      } else {
        window.$crisp?.do('chat:show');
      }
    }

    if (this.state.show !== prevState.show) {
      this.container?.scrollTo(0, 0);
    }
  }

  setSeparateBilling = (e) => {
    this.setState({ separateBilling: e.target.checked });
  };

  updateGivenName = (e) => {
    this.setState((state) => ({
      subscriptionInfo: { ...state.subscriptionInfo, givenname: e.target.value },
    }));
  };

  updateEmail = (e) => {
    this.setState((state) => ({
      subscriptionInfo: { ...state.subscriptionInfo, email: e.target.value },
    }));
  };

  updateCountry = (e) => {
    this.setState((state) => ({
      subscriptionInfo: { ...state.subscriptionInfo, country: e.target.value },
    }));
  };

  updateZipCode = (e) => {
    this.setState((state) => ({
      subscriptionInfo: { ...state.subscriptionInfo, postcode: e.target.value },
    }));
  };

  handleSubmit = async (e) => {
    e.preventDefault();
    const givenname = e.target.elements[0].value;
    const email = e.target.elements[1].value.toLowerCase();
    const billingemail = e.target.elements[4].checked ? e.target.elements[5].value : false;

    if (email && givenname) {
      const emailAllowed = await fetch(CHECK_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      })
        .then((response) => response.json())
        .catch(() => ({ exists: true, message: "Can't locate validation server, try again later." }));

      this.setState((state) => ({
        personData: { email, givenname, billingemail },
        subscriptionInfo: { ...state.subscriptionInfo, email, givenname },
      }));

      if (!emailAllowed.exists && !this.props.passTrialMessage) {
        this.setState({ show: SHOW.TRIAL });
      } else if (emailAllowed.canSubscribe) {
        this.openPaddleCheckout({ email, givenname, billingemail });
      } else {
        this.setState({
          status: 'error',
          message: `${emailAllowed.message}`,
        });
      }
    } else {
      this.setState({
        status: 'error',
        message: `All form fields are required`,
      });
    }
  };

  continueSubscribing = (e) => {
    e.preventDefault();
    this.openPaddleCheckout(this.state.personData);
  };

  openPaddleCheckout = (personData) => {
    const ref = this;

    const { email, givenname, billingemail } = personData;
    const { Paddle } = window;
    const paddleSettings = {
      email: billingemail || email,
      method: 'inline',
      frameTarget: 'checkout-container',
      frameInitialHeight: 337,
      frameStyle: 'position:relative !important;width:100%; background-color: #fff; border: none;',
      marketingConsent: 1,
      product: this.props.id,
      quantity: 1,
      quantity_variable: 0,
      allowQuantity: false,
      disableLogout: true,
      success: '/thanks',
      passthrough: JSON.stringify({
        givenname,
        email,
        ...(this.props.passThrough || {}),
      }),
      coupon: ref.props.coupon,
      country: this.state.subscriptionInfo.country,
      postcode: this.state.subscriptionInfo.postcode,
      customData: {
        tolt_referral: window.tolt_referral,
      },
    };

    if (this.props.customURL) {
      paddleSettings.override = this.props.customURL;
    }

    this.setState({ show: SHOW.PADDLE }, () => {
      console.log('Opening Paddle Checkout with settings:', paddleSettings);
      Paddle.Checkout.open(paddleSettings);
    });

    if (typeof window !== 'undefined' && window.umami) {
      window.umami.track((data) => ({ ...data, url: '/checkout/payment/', title: 'Paddle Checkout' }));
    }
  };

  hideForm = () => {
    if (typeof window !== 'undefined' && window.umami) {
      window.umami.track((data) => ({ ...data, url: '/checkout/closed/', title: 'Checkout closed' }));
    }

    this.props.setSignUpVisible({
      id: this.props.id,
      visible: false,
      name: this.props.name,
      priceYearly: this.props.priceYearly,
      total: this.propslocalPrice,
      priceInfo: this.props.priceInfo,
    });
    this.setState({ show: SHOW.FORM, status: '', message: '' });
  };

  render() {
    const { currency, visible, name, priceYearly, total, checkoutprice, priceInfo, coupon } = this.props;
    const { status, message, subscriptionInfo, show } = this.state;

    const duration = priceYearly ? 'year' : 'month';
    return (
      <StaticQuery
        query={detailsQuery}
        render={(data) => (
          <div
            className={[styles.container, visible && styles.visible].join(' ')}
            inert={!visible}
            aria-hidden={!visible}
            ref={(c) => {
              this.container = c;
            }}
          >
            <div className={styles.info}>
              <button
                type="button"
                aria-label="Close checkout"
                className={styles.close}
                onClick={this.hideForm}
                data-tip="Close checkout"
              >
                <Close width={16} height={16} id="" />
              </button>

              <div>
                <div className={styles.title}>
                  <img src={data.iconSVG.publicURL} alt="" title="" />
                  Polypane
                </div>
                <div className={styles.orderBlock}>
                  <small>Order details</small>

                  <div>
                    <div>
                      <h2>Polypane {name}</h2>
                      <small>{subscriptionInfo.email ? `for ${subscriptionInfo.email}` : <>&nbsp;</>}</small>
                    </div>
                    <div>
                      <b>{total}</b>
                      <small> per {duration} </small>
                    </div>
                  </div>
                  <p className={styles.shortdesc}>
                    <em>{tagline[name]}</em>
                  </p>
                </div>

                <table>
                  <tr>
                    <th>Subtotal</th>
                    <td>{priceInfo.net}</td>
                  </tr>
                  {coupon ? (
                    <tr>
                      <th>Discount code {priceInfo.discount ? '' : <small>not yet applied</small>}</th>
                      <td>{priceInfo.discount || `${coupon.slice(coupon.length - 2)}%`}</td>
                    </tr>
                  ) : null}
                  <tr className={styles.sales}>
                    <th>Sales tax {priceInfo.hasVat && priceInfo.taxrate ? <>({priceInfo.taxrate}%)</> : ''}</th>
                    <td>{priceInfo.vat}</td>
                  </tr>

                  <tr>
                    <th>
                      <b>
                        Total <em>{duration}ly</em>
                      </b>
                    </th>
                    <td>{total}</td>
                  </tr>
                  {checkoutprice && checkoutprice !== total ? (
                    <tr>
                      <th>
                        <b>This checkout: </b>
                      </th>
                      <td>{checkoutprice}</td>
                    </tr>
                  ) : null}

                  <caption>
                    price shown in <b>{currency}</b>
                  </caption>
                </table>
                <span>
                  <em aria-hidden={!priceInfo.hasVat}>Tax may be deducted after filling in company details.</em>
                </span>
              </div>
            </div>
            <div className={styles.form}>
              <div>
                <div className={styles.title}>
                  <img src={data.iconSVG.publicURL} alt="" title="" />
                  Polypane
                </div>

                <div className={styles.steps}>
                  Choose plan &gt; {[SHOW.FORM, SHOW.TRIAL].includes(show) ? <b>Your details</b> : <>Your details</>}{' '}
                  &gt; {[SHOW.PADDLE].includes(show) ? <b>Payment</b> : <>Payment</>}
                </div>

                {[SHOW.FORM, SHOW.TRIAL].includes(show) ? (
                  <form className={styles.contactForm} onSubmit={this.handleSubmit}>
                    {show === SHOW.TRIAL ? (
                      <div className={styles.noaccount}>
                        <h4>
                          The email address <strong>{this.state.personData.email}</strong> has no account.
                        </h4>
                        <p>Continue to subscribe right away, or start a free trial first.</p>
                        <div>
                          <a
                            href={`https://dashboard.polypane.app/register?email=${encodeURIComponent(
                              this.state.personData.email
                            )}&name=${encodeURIComponent(encodeURIComponent(this.state.personData.givenname))}`}
                          >
                            Start free trial
                          </a>
                          <button type="button" onClick={this.continueSubscribing}>
                            <span>Continue</span>
                          </button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <div className={styles.text}>
                          <h4>Please enter your details</h4>
                        </div>
                        <label htmlFor="givenname">First name</label>
                        <input
                          type="text"
                          name="givenname"
                          id="givenname"
                          autoComplete="given-name"
                          required
                          defaultValue={this.state.personData?.givenname}
                          onFocus={this.resetForm}
                          onInput={this.updateGivenName}
                          ref={(c) => {
                            this.firstInput = c;
                          }}
                        />
                        <label htmlFor="email">Email address</label>
                        <input
                          type="email"
                          name="email"
                          id="email"
                          autoComplete="email"
                          required
                          defaultValue={this.state.personData?.email}
                          onFocus={this.resetForm}
                          onInput={this.updateEmail}
                        />

                        <label htmlFor="country">Country</label>
                        <select
                          name="country"
                          id="country"
                          value={this.state.subscriptionInfo?.country}
                          onChange={this.updateCountry}
                        >
                          {countries.map((country) => (
                            <option key={country.code} value={country.code}>
                              {country.name}
                            </option>
                          ))}
                          >
                        </select>

                        {needZipCode.includes(this.state.subscriptionInfo.country) ? (
                          <>
                            <label htmlFor="zipcode">ZIP/Postcode</label>
                            <input
                              type="zipcode"
                              name="zipcode"
                              id="zipcode"
                              required
                              onFocus={this.resetForm}
                              onInput={this.updateZipCode}
                            />
                          </>
                        ) : null}

                        <label className={styles.topMargin} htmlFor="separatebilling">
                          <input
                            type="checkbox"
                            name="separatebilling"
                            id="separatebilling"
                            onChange={this.setSeparateBilling}
                          />{' '}
                          I want to use a separate billing email address
                        </label>

                        {this.state.separateBilling ? (
                          <>
                            <label htmlFor="billingemail">Billing email address</label>
                            <input
                              type="email"
                              defaultValue=""
                              name="billingemail"
                              id="billingemail"
                              autoComplete="email"
                              onFocus={this.resetForm}
                            />
                          </>
                        ) : null}

                        {status === 'error' ? (
                          <div className={styles.error} dangerouslySetInnerHTML={{ __html: message }} />
                        ) : (
                          <p />
                        )}

                        <button type="submit">
                          <span>Choose Payment Method</span>
                        </button>
                        <small>
                          <em>Add tax and company details in the next step.</em>
                        </small>
                      </>
                    )}
                  </form>
                ) : (
                  <div className={styles.checkoutContainer}>
                    <div
                      className={[
                        'checkout-container',
                        styles.paddleArea,
                        this.state.showTrialMessage || this.state.showForm ? styles.hidden : '',
                      ].join(' ')}
                    />
                  </div>
                )}
              </div>
            </div>
            <MobilePrice
              subtotal={priceInfo.net}
              vat={priceInfo.vat}
              total={total}
              checkout={checkoutprice}
              duration={duration}
              coupon={coupon}
              discount={priceInfo.discount}
              hasVat={priceInfo.hasVat}
              taxrate={priceInfo.taxrate}
            />
          </div>
        )}
      />
    );
  }
}

export default Index;

const detailsQuery = graphql`
  query checkoutPageQuery {
    iconSVG: file(relativePath: { eq: "images/icon.png" }) {
      publicURL
    }
  }
`;
